import React, { useState, useEffect } from 'react';
import { X, User, BookOpen, CheckCircle, XCircle, GraduationCap, Church, Heart, Users, Edit2, Plus } from 'lucide-react';
import type { Membership } from './MembershipsTable';
import { MembershipsService, type DatosComplementarios } from '../../services/memberships/memberships.service';
import { formatFechaLocal } from '../../utils/date.utils';
import { DatosComplementariosForm } from './DatosComplementariosForm';
import { NotificationToast } from '../ui/NotificationToast';
import { usePermissions } from '../../hooks/usePermissions';

interface MemberDetailsDrawerProps {
  isOpen: boolean;
  member: Membership | null;
  onClose: () => void;
}

type TabType = 'general' | 'adicionales';

export const MemberDetailsDrawer: React.FC<MemberDetailsDrawerProps> = ({
  isOpen,
  member,
  onClose
}) => {
  const { hasPermission } = usePermissions();
  const canEditMembership = hasPermission('Membresías', 'editar');
  const canCreateMembership = hasPermission('Membresías', 'crear');
  
  const [isVisible, setIsVisible] = useState(false);
  const [currentMember, setCurrentMember] = useState<Membership | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [datosComplementarios, setDatosComplementarios] = useState<DatosComplementarios | null>(null);
  const [loadingDatos, setLoadingDatos] = useState(false);
  const [errorDatos, setErrorDatos] = useState<string | null>(null);
  const [isEditingDatos, setIsEditingDatos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Notificación
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (isOpen && member) {
      setCurrentMember(member);
      setActiveTab('general');
      // Pequeño delay para asegurar que el componente se monte antes de la animación
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, member]);

  // Cargar datos complementarios cuando se selecciona la pestaña
  useEffect(() => {
    if (currentMember?.id && activeTab === 'adicionales') {
      loadDatosComplementarios(currentMember.id);
    }
  }, [activeTab, currentMember?.id]);

  const loadDatosComplementarios = async (memberId: string) => {
    setLoadingDatos(true);
    setErrorDatos(null);
    try {
      const datos = await MembershipsService.getDatosComplementarios(memberId);
      setDatosComplementarios(datos);
    } catch (err) {
      setErrorDatos('Error al cargar los datos complementarios');
      console.error(err);
    } finally {
      setLoadingDatos(false);
    }
  };

  const handleSaveDatosComplementarios = async (data: Partial<DatosComplementarios>) => {
    if (!currentMember?.id) return;
    
    setIsSubmitting(true);
    try {
      if (datosComplementarios) {
        // Actualizar existentes
        const updated = await MembershipsService.updateDatosComplementarios(currentMember.id, data);
        setDatosComplementarios(updated);
        setNotificationConfig({
          title: '¡Actualización exitosa!',
          message: 'Los datos complementarios han sido actualizados correctamente.',
          type: 'success'
        });
      } else {
        // Crear nuevos
        const created = await MembershipsService.createDatosComplementarios(currentMember.id, data);
        setDatosComplementarios(created);
        setNotificationConfig({
          title: '¡Registro exitoso!',
          message: 'Los datos complementarios han sido guardados correctamente.',
          type: 'success'
        });
      }
      setShowNotification(true);
      setIsEditingDatos(false);
    } catch (err: any) {
      setNotificationConfig({
        title: 'Error al guardar',
        message: err.message || 'No se pudieron guardar los datos complementarios.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Limpiar el currentMember después de la animación de salida
  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentMember(null);
        setDatosComplementarios(null);
        setActiveTab('general');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  // Timestamps reales (creadoEn, actualizadoEn): usar hora local normal
  const formatDate = (dateString: string) => {
    if (!dateString) return 'No especificado';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return 'No especificado';
    return new Date(dateTimeString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const calculateAge = (birthDate: string) => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const getNivelEstudiosLabel = (nivel?: string) => {
    const labels: Record<string, string> = {
      'primaria': 'Primaria',
      'secundaria': 'Secundaria',
      'tecnico': 'Técnico/Tecnólogo',
      'universitario': 'Universitario',
      'posgrado': 'Posgrado'
    };
    return labels[nivel || ''] || nivel || 'No especificado';
  };

  const getTiempoAsistenciaLabel = (tiempo?: string) => {
    const labels: Record<string, string> = {
      'menos_1_año': 'Menos de 1 año',
      '1_3_años': '1 a 3 años',
      'mas_3_años': 'Más de 3 años'
    };
    return labels[tiempo || ''] || tiempo || 'No especificado';
  };

  const getTipoPersonalidadLabel = (tipo?: string) => {
    const labels: Record<string, string> = {
      'extrovertido': 'Extrovertido',
      'introvertido': 'Introvertido',
      'cooperativo': 'Cooperativo',
      'intuitivo': 'Intuitivo'
    };
    return labels[tipo || ''] || tipo || 'No especificado';
  };

  if (!isOpen && !currentMember) return null;
  if (!currentMember) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay invisible para cerrar al hacer clic fuera */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Drawer - Ahora más ancho (max-w-4xl = 896px) */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xs font-medium text-gray-900">
                Detalles del Miembro
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Avatar y nombre - visible en ambas pestañas */}
              <div className="text-center py-6 bg-gradient-to-b from-gray-50 to-white">
                <div className="mx-auto h-24 w-24 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center mb-4 shadow-lg">
                  <span className="text-white text-xs font-medium">
                    {currentMember.nombres.charAt(0)}{currentMember.apellidos.charAt(0)}
                  </span>
                </div>
                <h3 className="text-xs font-medium text-gray-900 mb-1">
                  {currentMember.nombres} {currentMember.apellidos}
                </h3>
                <p className="text-gray-600 mb-2">{currentMember.email}</p>
                <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                  currentMember.estado === 'activo' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {currentMember.estado}
                </span>
              </div>

              {/* Tabs - después del avatar/nombre y antes del contenido */}
              <div className="flex border-b border-gray-200 bg-white px-6">
                <button
                  onClick={() => setActiveTab('general')}
                  className={`flex items-center px-6 py-3 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === 'general'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <User className="w-4 h-4 mr-2" />
                  Información General
                </button>
                <button
                  onClick={() => setActiveTab('adicionales')}
                  className={`flex items-center px-6 py-3 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === 'adicionales'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Datos Adicionales
                </button>
              </div>

              <div className="px-6 pb-6 pt-4">
                {/* TAB: INFORMACIÓN GENERAL */}
                {activeTab === 'general' && (
                  <div className="space-y-6">
                    {/* Información Personal */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                        Información Personal
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Fecha de Nacimiento</label>
                          <p className="text-gray-900 font-medium">{formatFechaLocal(currentMember.fechaNacimiento, { year: 'numeric', month: 'long', day: 'numeric' }, 'es-ES')}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Edad</label>
                          <div className="flex items-center space-x-2">
                            <p className="text-gray-900 font-medium">{calculateAge(currentMember.fechaNacimiento)} años</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              calculateAge(currentMember.fechaNacimiento) >= 18 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {calculateAge(currentMember.fechaNacimiento) >= 18 ? 'Mayor de edad' : 'Menor de edad'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Género</label>
                          <p className="text-gray-900 font-medium">{currentMember.genero}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Estado Civil</label>
                          <p className="text-gray-900 font-medium">{currentMember.estadoCivil}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Teléfono</label>
                          <p className="text-gray-900 font-medium">{currentMember.telefono}</p>
                        </div>
                      </div>
                    </div>

                    {/* Información de Contacto */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                        Información de Contacto
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Zona/Barrio</label>
                          <p className="text-gray-900 font-medium">{currentMember.zonaBarrio}</p>
                        </div>
                        {currentMember.numeroDocumento && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500">
                              Documento de Identidad
                            </label>
                            <p className="text-gray-900 font-medium">
                              {currentMember.tipoDocumento}: {currentMember.numeroDocumento}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Información de Registro */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                        Información de Registro
                      </h4>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-blue-700 mb-1">Fecha y Hora de Registro Original</label>
                            <p className="text-blue-900 font-medium">{formatDateTime(currentMember.marcaTemporal)}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="text-xs text-blue-700">
                              {new Date(currentMember.marcaTemporal).getTime() === new Date(currentMember.creadoEn).getTime() 
                                ? 'Registrado directamente en la aplicación' 
                                : 'Registrado originalmente en Excel y migrado'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Consentimientos */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                        Consentimientos
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full ${currentMember.consentimientoDatos ? 'bg-green-500' : 'bg-red-500'} mr-3`}></div>
                            <span className="text-xs font-medium">Consentimiento de datos</span>
                          </div>
                          <span className={`text-xs font-medium ${currentMember.consentimientoDatos ? 'text-green-600' : 'text-red-600'}`}>
                            {currentMember.consentimientoDatos ? 'Autorizado' : 'No autorizado'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full ${currentMember.autorizacionImagen ? 'bg-green-500' : 'bg-red-500'} mr-3`}></div>
                            <span className="text-xs font-medium">Autorización de imagen</span>
                          </div>
                          <span className={`text-xs font-medium ${currentMember.autorizacionImagen ? 'text-green-600' : 'text-red-600'}`}>
                            {currentMember.autorizacionImagen ? 'Autorizado' : 'No autorizado'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Observaciones Legales */}
                    {currentMember.observacionesLegales && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                          Observaciones Legales
                        </h4>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-gray-900 text-xs">{currentMember.observacionesLegales}</p>
                        </div>
                      </div>
                    )}

                    {/* Información del Sistema */}
                    <div className="pt-4 border-t border-gray-200">
                      <details className="cursor-pointer">
                        <summary className="text-xs font-medium text-gray-600 hover:text-gray-900">
                          Información del Sistema
                        </summary>
                        <div className="mt-3 space-y-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                          <div><span className="font-medium">ID:</span> {currentMember.id}</div>
                          <div><span className="font-medium">Registrado:</span> {formatDate(currentMember.creadoEn)}</div>
                          <div><span className="font-medium">Última actualización:</span> {formatDate(currentMember.actualizadoEn)}</div>
                          <div><span className="font-medium">Marca temporal del registro:</span> {formatDateTime(currentMember.marcaTemporal)}</div>
                        </div>
                      </details>
                    </div>
                  </div>
                )}

                {/* TAB: DATOS ADICIONALES */}
                {activeTab === 'adicionales' && (
                  <div className="space-y-6">
                    {loadingDatos ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        <span className="ml-3 text-gray-600">Cargando datos adicionales...</span>
                      </div>
                    ) : errorDatos ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-700">{errorDatos}</p>
                      </div>
                    ) : isEditingDatos ? (
                      /* Formulario de edición */
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-xs font-medium text-gray-900">
                            {datosComplementarios ? 'Editar Datos Adicionales' : 'Agregar Datos Adicionales'}
                          </h4>
                          <button
                            onClick={() => setIsEditingDatos(false)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <DatosComplementariosForm
                          initialData={datosComplementarios}
                          onSubmit={handleSaveDatosComplementarios}
                          onCancel={() => setIsEditingDatos(false)}
                          isSubmitting={isSubmitting}
                        />
                      </div>
                    ) : !datosComplementarios ? (
                      /* Sin datos - mostrar botón para agregar */
                      <div className="text-center py-12">
                        <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                          <BookOpen className="w-8 h-8 text-gray-400" />
                        </div>
                        <h4 className="text-xs font-medium text-gray-900 mb-2">Sin datos adicionales</h4>
                        <p className="text-gray-500 max-w-sm mx-auto mb-4">
                          Este miembro aún no tiene datos complementarios registrados.
                        </p>
                        {canCreateMembership && (
                          <button
                            onClick={() => setIsEditingDatos(true)}
                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Agregar Datos Adicionales
                          </button>
                        )}
                      </div>
                    ) : (
                      /* Mostrar datos con botón de editar */
                      <>
                        <div className="flex justify-end mb-4">
                          {canEditMembership && (
                            <button
                              onClick={() => setIsEditingDatos(true)}
                              className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Editar Información
                            </button>
                          )}
                        </div>

                        {/* Información Académica y Profesional */}
                        <div>
                          <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                            <GraduationCap className="w-5 h-5 mr-2 text-purple-600" />
                            Información Académica y Profesional
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Nivel de Estudios</label>
                              <p className="text-gray-900 font-medium">{getNivelEstudiosLabel(datosComplementarios.nivelEstudios)}</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Profesión/Ocupación</label>
                              <p className="text-gray-900 font-medium">{datosComplementarios.profesion || 'No especificado'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Información Espiritual */}
                        <div>
                          <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                            <Church className="w-5 h-5 mr-2 text-purple-600" />
                            Información Espiritual
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Tiempo en la Iglesia</label>
                              <p className="text-gray-900 font-medium">{getTiempoAsistenciaLabel(datosComplementarios.tiempoAsistencia)}</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500">¿Es Bautizado?</label>
                              <div className="flex items-center mt-1">
                                {datosComplementarios.esBautizado ? (
                                  <><CheckCircle className="w-5 h-5 text-green-500 mr-2" /><span className="text-green-700 font-medium">Sí</span></>
                                ) : (
                                  <><XCircle className="w-5 h-5 text-red-500 mr-2" /><span className="text-red-700 font-medium">No</span></>
                                )}
                              </div>
                            </div>
                            {datosComplementarios.esBautizado && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Año de Bautismo</label>
                                  <p className="text-gray-900 font-medium">{datosComplementarios.añoBautismo || 'No especificado'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Iglesia de Bautismo</label>
                                  <p className="text-gray-900 font-medium">{datosComplementarios.iglesiaBautismo || 'No especificado'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Ministro que Bautizó</label>
                                  <p className="text-gray-900 font-medium">{datosComplementarios.ministroBautismo || 'No especificado'}</p>
                                </div>
                              </>
                            )}
                            <div>
                              <label className="block text-xs font-medium text-gray-500">¿Oración de Fe?</label>
                              <div className="flex items-center mt-1">
                                {datosComplementarios.oracionFe ? (
                                  <><CheckCircle className="w-5 h-5 text-green-500 mr-2" /><span className="text-green-700 font-medium">Sí</span></>
                                ) : (
                                  <><XCircle className="w-5 h-5 text-red-500 mr-2" /><span className="text-red-700 font-medium">No</span></>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Estudios Realizados */}
                        {datosComplementarios.estudiosRealizados && datosComplementarios.estudiosRealizados.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                              Estudios Realizados en la Congregación
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {datosComplementarios.estudiosRealizados.map((estudio, index) => (
                                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {estudio}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Personalidad y Habilidades */}
                        <div>
                          <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                            <Heart className="w-5 h-5 mr-2 text-purple-600" />
                            Personalidad y Habilidades
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Tipo de Personalidad</label>
                              <p className="text-gray-900 font-medium">{getTipoPersonalidadLabel(datosComplementarios.tipoPersonalidad)}</p>
                            </div>
                          </div>
                          
                          {datosComplementarios.habilidades && datosComplementarios.habilidades.length > 0 && (
                            <div className="mb-4">
                              <label className="block text-xs font-medium text-gray-500 mb-2">Habilidades</label>
                              <div className="flex flex-wrap gap-2">
                                {datosComplementarios.habilidades.map((habilidad, index) => (
                                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {habilidad}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {datosComplementarios.dones && datosComplementarios.dones.length > 0 && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-2">Dones Espirituales</label>
                              <div className="flex flex-wrap gap-2">
                                {datosComplementarios.dones.map((don, index) => (
                                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    {don}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Ministerios */}
                        <div>
                          <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                            <Users className="w-5 h-5 mr-2 text-purple-600" />
                            Participación en Ministerios
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">¿Pertenece a algún Ministerio?</label>
                              <div className="flex items-center mt-1">
                                {datosComplementarios.perteneceMinisterio ? (
                                  <><CheckCircle className="w-5 h-5 text-green-500 mr-2" /><span className="text-green-700 font-medium">Sí</span></>
                                ) : (
                                  <><XCircle className="w-5 h-5 text-red-500 mr-2" /><span className="text-red-700 font-medium">No</span></>
                                )}
                              </div>
                            </div>
                            {datosComplementarios.ministerioDeseado && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Ministerio de Interés</label>
                                <p className="text-gray-900 font-medium">{datosComplementarios.ministerioDeseado}</p>
                              </div>
                            )}
                          </div>
                          
                          {datosComplementarios.perteneceMinisterio && datosComplementarios.ministeriosActuales && datosComplementarios.ministeriosActuales.length > 0 && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-2">Ministerios Actuales</label>
                              <div className="flex flex-wrap gap-2">
                                {datosComplementarios.ministeriosActuales.map((ministerio, index) => (
                                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    {ministerio}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Fecha de la Encuesta */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <label className="block text-xs font-medium text-gray-500">Fecha de la Encuesta</label>
                          <p className="text-gray-900 font-medium">{formatFechaLocal(datosComplementarios.fechaEncuesta, { year: 'numeric', month: 'long', day: 'numeric' }, 'es-ES')}</p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default MemberDetailsDrawer;
